import React, { useMemo } from 'react'
import { useSelector } from '@/store'
import { QUERY_CATEGORY, STYLING_CATEGORY, WidgetDescription } from '@/helpers/widget'
import { allWidgetDefinitionsSorted } from '@/components/widgets'
import { Button, Form } from '@/components/ui-primitives/small'
import { ViewerConfig } from "@/components/Viewer"
import { HostedViewerCtx, useHostedViewerCtx } from "@/hooks/use-app-ctx"
import { useSyncSignal } from '@/hooks/use-sync-signal'
import { FieldFeedback, useWistorEditorController } from '@/components/editor/widget-editor/editor-controller'
import { AnyWidgetDefinition, WidgetConfig } from '@/components/WidgetConfiguration'
import { handleAddWidget, widgetPathToIndices } from '@/components/editor/widget-tree'
import { WidgetSelectorHandle } from '@/components/editor/widget-selector/widget-selector'
import { Braces, CirclePlus, SquareMousePointer } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { toast } from 'sonner'
import invariant from 'tiny-invariant'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-primitives/tabs'
import { ACCENT } from '@/components/editor/accent'
import { EditorTab, focusField, useEditorTabStore, usePendingFocus } from '@/components/editor/widget-editor/editor-tabs'
import { WidgetSwitcher } from '@/components/editor/widget-editor/widget-switcher'
import { QueryTab } from '@/components/editor/widget-editor/query-tab'
import {
  COMMON_TAB_VARIABLES,
  CommonTab,
  PanelStylingSection,
  panelStylingVariables,
} from '@/components/editor/widget-editor/common-tab'
import {
  AdvancedOptionsToggle,
  AnyConfigDesc,
  SettingsSection,
  VariableFields,
  WidgetFieldsForm,
} from '@/components/editor/widget-editor/form-fields'

export function WidgetEditorForm() {
  const viewerCtx = useHostedViewerCtx()
  const viewerConfig = useSelector(s => s.currentViewer?.config)
  const widgetSelector = viewerCtx.widgetSelectorRef.current
  if (!viewerConfig || !widgetSelector) return null

  return (
    <WidgetEditorFormImpl
        viewerConfig={viewerConfig}
        viewerCtx={viewerCtx}
        widgetSelector={widgetSelector} />
  )
}

function WidgetEditorFormImpl(props: {
  viewerConfig: ViewerConfig
  viewerCtx: HostedViewerCtx
  widgetSelector: WidgetSelectorHandle
}) {
  const { configDraft, selectedInfo } = useWistorEditorController(props.viewerConfig, props.viewerCtx)

  const isPicking = useSyncSignal(props.widgetSelector.startedSignal, props.widgetSelector.started())
  usePendingFocus() // here, as this stays mounted while a just-added widget gets its form

  const startOrStopPicking = () => {
    if (props.widgetSelector.started()) props.widgetSelector.cancel()
    else props.widgetSelector.start()
  }

  return (
    <Form className="!p-0 gap-0">
      <WidgetSwitcher viewerConfig={props.viewerConfig}
                      configDraft={configDraft}
                      isPicking={isPicking}
                      onTogglePicking={startOrStopPicking} />
      {configDraft ? (
        <WidgetFormFields viewerConfig={props.viewerConfig}
                          viewerCtx={props.viewerCtx}
                          configDraft={configDraft} />
      ) : (
        <div className="flex flex-col items-center justify-center">
          {(selectedInfo.path && !selectedInfo.isInitialized) ? (
            <>
              <div className="my-3 text-gray-500">There is no widget here yet!</div>
              <Button variant="outline"
                      onClick={() => {
                        handleAddWidget(props.viewerConfig, selectedInfo.path!)
                        focusField("common", "widget-formfield")
                      }}>
                <CirclePlus size={18} />
                Add widget.
              </Button>
            </>
          ) : (
            <>
            <div className="my-3 text-gray-500">or</div>
              <Button variant="outline"
                      className={mergeClasses({
                        'ring-2 animate-pulse': isPicking,
                      })}
                      onClick={startOrStopPicking}>
                <SquareMousePointer size={18} />
                {isPicking ? 'Picking widget…' : 'Pick widget'}
              </Button>
            </>
          )}
        </div>
      )}
    </Form>
  )
}

function WidgetFormFields(props: {
  viewerConfig: ViewerConfig
  viewerCtx: HostedViewerCtx
  configDraft: WidgetConfig
}) {
  const {
    configDraft,
    updateField,
    selectedPath,
    feedback,
    setFeedback,
  } = useWistorEditorController(props.viewerConfig, props.viewerCtx)

  invariant(configDraft, 'Config cannot be null here')

  const form: WidgetFieldsForm = {
    configDraft,
    updateField,
    localHistoryKeyPrefix: localHistoryKeyPrefixFor(selectedPath, configDraft.widget),
    feedback,
    setFeedback,
  }

  const widgetDef = useWidgetDefinition(configDraft.widget)
  const widgetName = widgetDef?.name ?? configDraft.widget ?? ""

  const variables = useWidgetVariables(widgetDef, configDraft)
  const { queryVariables, stylingVariables, widgetGroups } = useMemo(() => {
    const {
      [QUERY_CATEGORY]: queryVariables = [],
      [STYLING_CATEGORY]: stylingVariables = [],
      ...widgetGroups
    } = smartlyGroup(variables)
    return { queryVariables, stylingVariables, widgetGroups: Object.entries(widgetGroups) }
  }, [ variables ])
  const widgetTabVariables = useMemo(() => widgetGroups.flatMap(([ , variables ]) => variables), [ widgetGroups ])
  const stylingTabVariables = useMemo(
    () => [ ...stylingVariables, ...panelStylingVariables(configDraft.widget) ],
    [ stylingVariables, configDraft.widget ]
  )

  const tabs: { id: EditorTab, label: React.ReactNode }[] = [
    ...(queryVariables.length ? [ { id: "query" as const, label: <><Braces size={13} />Query</> } ] : []),
    ...(widgetGroups.length ? [ { id: "widget" as const, label: widgetName } ] : []),
    { id: "common", label: "Common" },
    { id: "styling", label: "Styling" },
  ]
  const preferredTab = useEditorTabStore(s => s.tab)
  const activeTab = tabs.some(tab => tab.id === preferredTab) ? preferredTab : tabs[0].id

  return (
    <div className="flex flex-col">
      <UnappliedBecauseOfErrors feedback={feedback} variables={variables} />
      <Tabs value={activeTab}
            onValueChange={tab => useEditorTabStore.setState({ tab: tab as EditorTab })}>
        <TabsList className="sticky top-12 z-20 flex h-auto w-full flex-wrap justify-start gap-0.5 rounded-none border-b border-border bg-white0 p-0 px-2">
          {tabs.map(tab => (
            <TabsTrigger key={tab.id}
                         value={tab.id}
                         className={mergeClasses(`
                           -mb-px gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2
                           font-exo text-[13px] text-muted-foreground hover:text-foreground
                           data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:shadow-none
                           focus-visible:ring-inset focus-visible:ring-offset-0 focus-visible:ring-[#51479e]/40`,
                           ACCENT.activeTab)}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="query" className="mt-0 flex flex-col">
          <QueryTab form={form} selectedPath={selectedPath} variables={queryVariables} />
          <AdvancedOptionsToggle variables={queryVariables} className="m-3" />
        </TabsContent>

        <TabsContent value="widget" className="mt-0 flex flex-col gap-2 py-3">
          {widgetGroups.map(([ category, variables ]) => category ? (
            <SettingsSection
              key={category}
              id={`widget-specfic-${category}`}
              header={category}
              form={form}
              variables={variables}
              defaultExpanded />
          ) : (
            <div key="uncategorized" className="flex flex-col gap-2 px-3">
              <VariableFields form={form} variables={variables} />
            </div>
          ))}
          <AdvancedOptionsToggle variables={widgetTabVariables} className="mx-3" />
        </TabsContent>

        <TabsContent value="common" className="mt-0 flex flex-col">
          <CommonTab form={form} />
          <AdvancedOptionsToggle variables={COMMON_TAB_VARIABLES} className="m-3" />
        </TabsContent>

        <TabsContent value="styling" className="mt-0 flex flex-col">
          <SettingsSection id="widget-styling-own"
                           header={widgetName}
                           form={form}
                           variables={stylingVariables}
                           defaultExpanded />
          <PanelStylingSection form={form} />
          <AdvancedOptionsToggle variables={stylingTabVariables} className="m-3" />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** Explains why edits do not reach the page while a field reports an error */
function UnappliedBecauseOfErrors(props: {
  feedback: Record<string, FieldFeedback>
  variables: ReadonlyArray<AnyConfigDesc>
}) {
  const fields = Object.entries(props.feedback)
    .filter(([ , f ]) => f.kind === 'error')
    .map(([ name ]) => fieldTitle(props.variables.find(v => v.name === name)) ?? name)
  if (!fields.length) return null
  return (
    <div role="status" className="mx-2 mt-2 rounded-md bg-[#fde8e8] px-2.5 py-1.5 text-xs text-[#9b1c1c]">
      <span className="font-semibold">Not applied:</span> fix the error in {fields.join(', ')}. Your other edits to this widget apply once it is fixed.
    </div>
  )
}

/** First line of a variable's label, which for some options is a paragraph */
function fieldTitle(variable: AnyConfigDesc | undefined) {
  if (!variable || typeof variable.label !== 'string') return undefined
  return variable.label.split('\n')[0].trim() || undefined
}

/**
 * The descriptor's options plus any the widget derives from its current config
 * (CodeWidget: the fields its page HTML declares, stored under "configField:").
 */
function useWidgetVariables(widgetDef: WidgetDescription<any, any, any> | null, configDraft: WidgetConfig) {
  const deriveExtraVariables = (widgetDef as AnyWidgetDefinition | null)?.deriveExtraVariables
  const derived = useMemo(
    () => deriveExtraVariables?.(configDraft as Record<string, unknown>) ?? [],
    [ deriveExtraVariables, configDraft ]
  )
  return useMemo(() => [ ...(widgetDef?.variables ?? []), ...derived ], [ widgetDef?.variables, derived ])
}

function useWidgetDefinition(widget: string | undefined) {
  return useMemo(() => {
    if (!widget) return null
    const widgetDefinition = allWidgetDefinitionsSorted.find((w) => w.name === widget) as WidgetDescription<any, any, any>
    if (!widgetDefinition) {
      toast.error(`Widget ${widget} does not exist in the current version of Wistor`)
    }
    return widgetDefinition ?? null
  }, [ widget ])
}

/**
 * Same key as the old WidgetConfiguration dialog builds from its area path ("main.0.1"), so the
 * per-field editor history of a widget is shared between the old and the new configurator UX.
 */
function localHistoryKeyPrefixFor(selectedPath: string | null, widget: string | undefined) {
  if (!selectedPath) return undefined
  const areaPath = ['main', ...widgetPathToIndices(selectedPath)].join('.')
  return `${window.location.pathname.replace(/\/edit$|\/debug$/, '')}:${areaPath}:${widget ?? 'widget'}`
}

function smartlyGroup(variables: ReadonlyArray<AnyConfigDesc>): Record<string, AnyConfigDesc[]> {
  const byCategory: Record<string, AnyConfigDesc[]> = {}
  for (const variable of variables) {
    let category = ""
    if (variable.type === "yasgui") category = QUERY_CATEGORY
    if (variable.category) category = variable.category
    if (!(category in byCategory)) byCategory[category] = []
    byCategory[category].push(variable)
  }
  return byCategory
}
